import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";

import { ReadonlyAbortNotifier } from "./AbortNotifier";
import {
  getFaireImageIdentifier,
  getSetOfImageIdentifiers,
} from "./getSetOfImageIdentifiers";

export const waitForImagesToLoad = (
  targetElements: Array<HTMLElement>,
  abortNotifier: ReadonlyAbortNotifier<unknown>,
  onImageLoad?: (numImagesPending: number, totalNumImages: number) => void,
  shouldIncludeAllImages?: boolean
): Promise<{ numImages: number; lastImageLoadTime: DOMHighResTimeStamp }> => {
  const { HTMLImageElement, performance } = getWindowOrThrow();
  return new Promise((resolve) => {
    const { numImages, pendingImageUrls } = getSetOfImageIdentifiers(
      targetElements,
      shouldIncludeAllImages
    );
    let lastImageLoadTime = performance.now();
    const totalVisibleImages = pendingImageUrls.size;

    const onLoadOrError = (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLImageElement) {
        const wasInSet = pendingImageUrls.delete(
          getFaireImageIdentifier(target)
        );
        if (wasInSet) {
          lastImageLoadTime = performance.now();
        }
      }
      onImageLoad?.(pendingImageUrls.size, totalVisibleImages);
      if (pendingImageUrls.size === 0) {
        removeListeners();
        resolve({ numImages, lastImageLoadTime });
      }
    };

    const removeListeners = () => {
      targetElements.forEach((element) => {
        element.removeEventListener("load", onLoadOrError, { capture: true });
        element.removeEventListener("error", onLoadOrError, { capture: true });
      });
    };

    if (targetElements.length === 0 || pendingImageUrls.size === 0) {
      resolve({ numImages, lastImageLoadTime });
      onImageLoad?.(0, totalVisibleImages);
    } else {
      targetElements.forEach((element) => {
        element.addEventListener("load", onLoadOrError, { capture: true });
        element.addEventListener("error", onLoadOrError, { capture: true });
      });
      abortNotifier?.addAbortListener(() => {
        removeListeners();
        resolve({ numImages, lastImageLoadTime });
      });
    }
  });
};
